import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { useApplicationsReport } from '@/hooks/useApplicationsReport';
import { useAppContext } from '@/context/AppContext';
import { formatDayShort } from '@/lib/dates';
import type { ApplicationsReportRow } from '@/types/reports';
import styles from './ApplicationsReportPage.module.css';

// `app` is the non-sortable row-label column; the rest are click-sortable.
type ColumnKey =
  | 'app'
  | 'distinctUsers'
  | 'totalEvents'
  | 'avgHoursPerUser'
  | 'billableHours'
  | 'nonBillableHours'
  | 'totalHours';

type SortKey = Exclude<ColumnKey, 'app'>;

interface SortState {
  key: SortKey;
  direction: 'asc' | 'desc';
}

const DEFAULT_SORT: SortState = { key: 'totalHours', direction: 'desc' };

/**
 * Applications report page — task 117.
 *
 * Lists every application that had activity across the team in the selected
 * date range. Each row shows distinct users, event count, average hours per
 * user, billable / non-billable split, and total hours. Server owns the
 * default sort (totalHours DESC); numeric columns are click-to-sort on the
 * client.
 */
export function ApplicationsReportPage() {
  const { data } = useApplicationsReport();
  const { dateRange } = useAppContext();
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const rows = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp = av === bv ? 0 : av < bv ? -1 : 1;
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [data, sort]);

  const onSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' },
    );
  };

  const windowLabel =
    dateRange.start && dateRange.end
      ? `${formatDayShort(dateRange.start)} – ${formatDayShort(dateRange.end)}`
      : '';
  const subtitle = `Where your team's time goes across ${data.length} tool${
    data.length === 1 ? '' : 's'
  }${windowLabel ? `, ${windowLabel}` : ''}`;

  // Task 156 D3 — drill-down link helper. The app row is the only
  // navigable cell on this page; clicking lands on the Monthly Report
  // pre-scoped to the active range, with `?app=<name>` as a forward-
  // compat marker the report page is expected to respect once an `app`
  // filter ships (see task body §D3 — neither of the existing surfaces
  // supports the filter today, so the link is "the row that takes the
  // user the closest to the filtered view we can build").
  const buildAppHref = (app: string): string => {
    const startDate = dateRange.start;
    const endDate = dateRange.end;
    const params = new URLSearchParams({
      period: 'custom',
      startDate,
      endDate,
      app,
    });
    return `/reports/monthly?${params.toString()}`;
  };

  return (
    <>
      <PageHeader title="Applications" subtitle={subtitle} />

      <DataTable<ApplicationsReportRow, ColumnKey>
        caption="Applications report"
        sort={sort}
        onSort={(key) => {
          // DataTable hands the non-sortable column's key back when the
          // user clicks its header; that column isn't sortable so ignore.
          if (key === 'app') return;
          onSort(key);
        }}
        columns={[
          {
            key: 'app',
            header: 'Application Name',
            render: (r) => (
              <Link to={buildAppHref(r.app)} className={styles.appCell} data-testid="app-row-link">
                {r.app}
              </Link>
            ),
          },
          {
            key: 'distinctUsers',
            header: 'People',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.distinctUsers}</span>,
          },
          {
            key: 'totalEvents',
            header: 'Total Events',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.totalEvents.toLocaleString()}</span>,
          },
          {
            key: 'avgHoursPerUser',
            header: 'Avg Time / Person',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.avgHoursPerUser.toFixed(1)}h</span>,
          },
          {
            key: 'billableHours',
            header: 'Billable',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.billableHours.toFixed(1)}h</span>,
          },
          {
            key: 'nonBillableHours',
            header: 'Non-Billable',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.nonBillableHours.toFixed(1)}h</span>,
          },
          {
            key: 'totalHours',
            header: 'Total Time',
            align: 'right',
            sortable: true,
            render: (r) => <span className={styles.numCell}>{r.totalHours.toFixed(1)}h</span>,
          },
        ]}
        rows={rows}
        getRowKey={(r) => r.app}
        emptyState={
          <span className={styles.emptyState}>
            No tool activity from your team in this date range.
          </span>
        }
      />
    </>
  );
}
